import * as React from 'react';
import { RotateCw, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

import { fileToBase64 } from '@/components/apple-pay/byok-ui';

/**
 * Advanced path for users who ALREADY hold their Apple certificates (issued
 * against their own signing request, so the private key lives in their
 * Keychain): they export each certificate together with its key as a
 * password-protected `.p12` and upload the pair here.
 *
 * A bare `.cer` is NOT enough on this path — without the private key a p12
 * cannot exist. Users who only have `.cer` files (or no certificates yet)
 * belong on the guided signing-request flow instead.
 */
export interface P12UploadPayload {
  paymentProcessorCertificateData: string;
  paymentProcessorCertificatePassword: string;
  merchantCertificateData?: string;
  merchantCertificatePassword?: string;
  domain?: string;
}

export function P12UploadForm({
  busy,
  replacing,
  onSubmit,
}: {
  busy: boolean;
  /** True when this upload replaces an existing certificate (rotation). */
  replacing?: boolean;
  onSubmit: (payload: P12UploadPayload) => Promise<void>;
}) {
  const [processingFile, setProcessingFile] = React.useState<File | null>(null);
  const [processingPassword, setProcessingPassword] = React.useState('');
  const [identityFile, setIdentityFile] = React.useState<File | null>(null);
  const [identityPassword, setIdentityPassword] = React.useState('');
  const [domain, setDomain] = React.useState('');

  const canSubmit = !!processingFile && !!processingPassword && !busy;

  const handleSubmit = async () => {
    if (!processingFile) return;
    await onSubmit({
      paymentProcessorCertificateData: await fileToBase64(processingFile),
      paymentProcessorCertificatePassword: processingPassword,
      ...(identityFile
        ? {
            merchantCertificateData: await fileToBase64(identityFile),
            merchantCertificatePassword: identityPassword,
          }
        : {}),
      ...(domain.trim() ? { domain: domain.trim() } : {}),
    });
    setProcessingFile(null);
    setProcessingPassword('');
    setIdentityFile(null);
    setIdentityPassword('');
    setDomain('');
  };

  return (
    <div className="bg-muted/30 space-y-4 rounded-md border p-4">
      <p className="text-sm font-medium">
        {replacing ? 'Upload replacement .p12 certificates' : 'Upload your .p12 certificates'}
      </p>
      <p className="text-muted-foreground text-xs">
        Export each certificate <strong>with its private key</strong> from Keychain Access as a
        password-protected <code>.p12</code> (a bare <code>.cer</code> is not enough — it has no key). The
        payment processing certificate must be <strong>EC P-256</strong>. Files and passwords go straight to
        our tokenization provider and are never stored.
      </p>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label className="text-xs">Payment processing .p12 (required)</Label>
          <Input
            type="file"
            accept=".p12,.pfx"
            onChange={(e) => setProcessingFile(e.target.files?.[0] ?? null)}
          />
          <Input
            type="password"
            placeholder="Processing .p12 password"
            value={processingPassword}
            onChange={(e) => setProcessingPassword(e.target.value)}
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Merchant identity .p12 (for web)</Label>
          <Input
            type="file"
            accept=".p12,.pfx"
            onChange={(e) => setIdentityFile(e.target.files?.[0] ?? null)}
          />
          <Input
            type="password"
            placeholder="Identity .p12 password"
            value={identityPassword}
            onChange={(e) => setIdentityPassword(e.target.value)}
          />
        </div>
      </div>

      <div className="space-y-1.5">
        <Label className="text-xs">Apple-verified domain to bind (optional)</Label>
        <Input
          placeholder="checkout.example.com"
          value={domain}
          onChange={(e) => setDomain(e.target.value)}
          className="font-mono text-sm"
        />
      </div>

      <Button onClick={handleSubmit} disabled={!canSubmit} size="sm">
        {replacing ? <RotateCw className="mr-2 h-4 w-4" /> : <Upload className="mr-2 h-4 w-4" />}
        {busy ? 'Uploading…' : replacing ? 'Upload & replace' : 'Upload certificates'}
      </Button>
    </div>
  );
}
